<template>
  <div
    class="msg-file"
    @click.prevent="viewMedia"
  >
    <div class="info" :class="canDownload? 'file-pointer' : ''">
      <div class="icon">
        <img :src="require(`./filetype/${fileFormat}.png`)" />
      </div>
      <div class="detail">
        <div class="name" :title="data.fileName">{{ data.fileName }}</div>
        <div class="flex">
          <div class="size">{{ bytesToSize(data.fileSize) }}</div>
          <div class="percent" v-show="showProgress">{{ Math.floor(data.uploadProgress*100) + '%' }}</div>
        </div>
      </div>
      <div class="operator" v-if="showProgress || canDownload">
        <!-- 正在上传，且有控制器时，则显示停止上传操作 -->
        <img v-if="showProgress && data.progressController"
          src="./assets/icon-cancle-upload.svg" @click.stop="stopUpload"/>
        <img v-if="canDownload" src="./assets/icon-file-download.svg" @click.stop="downloadFile"/>
      </div>
      <div v-show="showProgress" class="progress" :style="`width:${data.uploadProgress*100}%`"></div>
    </div>
  </div>
</template>

<script>
import Utils from '@/utils/file';
import { IMSDK, SDKUtils } from '@/imsdk/index.ts';
import { isWXWork, isWeixin, isQQ } from '@/utils/isWX';
import { GET_FILE_DOWNLOAD_URL_FOR_OLD_MSG } from '@/utils/api';
import { base64 } from '@tencent/qd-lib';

export default {
  inject: ['MsgStatus', 'wpaDetail'],
  props: {
    data: Object,
  },
  data() {
    return {
      formatPath: '',
    };
  },
  computed: {
    // 是否显示上传进度
    showProgress() {
      return this.data.uploadProgress && this.data.uploadProgress < 1 && this.data.status === this.MsgStatus.Sending;
    },
    formatTime() {
      const date = new Date(this.data.time);
      return date.toLocaleDateString() + date.toLocaleTimeString();
    },
    fileFormat() {
      if (/(.mp3|.wav)$/i.test(this.data.fileName)) {
        return 'audio';
      }
      if (/(.mov|.mp4|.avi)$/i.test(this.data.fileName)) {
        return 'video';
      }
      if (/(.jpg|.jpeg|.png|.gif|.bmp)$/i.test(this.data.fileName)) {
        return 'picture';
      }
      if (/.txt$/i.test(this.data.fileName)) {
        return 'txt';
      }
      if (/.pdf$/i.test(this.data.fileName)) {
        return 'pdf';
      }
      if (/(.zip|.rar)$/i.test(this.data.fileName)) {
        return 'zip';
      }
      if (/(.doc|.docx)$/i.test(this.data.fileName)) {
        return 'word';
      }
      if (/(.xls|.xlsx)$/i.test(this.data.fileName)) {
        return 'excel';
      }
      if (/(.ppt|.pptx)$/i.test(this.data.fileName)) {
        return 'ppt';
      }
      return 'unknown';
    },

    // 下载链接
    downloadUrl() {
      let url = this.formatPath;
      if (this.data.uuid !== 'blob') {
        // if (this.data.fileName) {
        //   url = `${
        //     this.formatPath
        //   }&response-content-disposition=${encodeURIComponent(`attachment; filename=${this.data.fileName}`)}`;
        // } else {
        //   url = `${
        //     this.formatPath
        //   }&response-content-disposition=${encodeURIComponent('attachment;')}`;
        // }
        // 去掉了Saas上下载文件附加文件名的逻辑。如果附加会导致签名验证失败。。。

        if (window.type_s3_protocol === 'COS') {
          // COS 存储的时候，必须拼接名称，否则下载的文件没有名称。
          url = `${this.formatPath}&response-content-disposition=${encodeURIComponent(`attachment; filename=${this.data.fileName}`)}`;
        }
      }

      return url;
    },

    // blob文件在移动端会存在跨环境的情况（如用户在微信中复制的url，要去浏览器下载），无法下载。
    canDownload() {
      return (this.data.uuid !== 'blob') && (this.data.status === this.MsgStatus.Success) && this.downloadUrl;
    },
  },
  async mounted() {
    // 如果是本地blob图片，或者是外链其他图片，则直接获取
    if (this.data.uuid === 'blob' || this.data.path.indexOf('http') === 0) {
      this.formatPath = this.data.path;
      return;
    }

    // 获得存储的文件路径
    if (this.data.isOldRoamingMsg) {
      this.formatPath = await this.getDownloadUrlForOldMsg();
    } else {
      this.formatPath = await SDKUtils.getDownloadUrl({
        path: this.data.path,
        size: this.data.fileSize,
        name: this.data.fileName,
      }, IMSDK.OBJTYPES.OBJ_FILE);
    }
  },
  methods: {
    bytesToSize(bytes) {
      if (isNaN(bytes) || bytes === 0) return '0B';
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
      const k = 1024;
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return (bytes / (k ** i)).toPrecision(3) + sizes[i];
    },

    /**
     * 在线查看媒体文件（图片，mp3,mp4）
     */
    viewMedia() {
      if (!this.canDownload) {
        return;
      }

      const url = this.downloadUrl;
      const CAN_PLAY_MEDIA_TYPE = {
        audio: ['.mp3'],
        video: ['.mp4'],
        image: ['.gif', '.jpg', '.jpeg', '.bmp', '.png'],
      };
      const filename = this.data.fileName || '';
      const fileType = filename.substr(filename.lastIndexOf('.')).toLowerCase();

      if (CAN_PLAY_MEDIA_TYPE.video.indexOf(fileType) > -1) {
        // 视频
        this.$MediaViewer.show({ url, type: 'video', downloadFunc: () => {
          this.downloadFile();
        } });
      } else if (CAN_PLAY_MEDIA_TYPE.audio.indexOf(fileType) > -1) {
        // 音频
        this.$MediaViewer.show({ url, type: 'audio', downloadFunc: () => {
          this.downloadFile();
        } });
      } else if (CAN_PLAY_MEDIA_TYPE.image.indexOf(fileType) > -1) {
        // 图片
        this.$PictureViewer.show({
          showPhotoPreView: true,
          photoUrls: [url],
          photoPreviewIndex: [url].length - 1,
          closeOnClickMask: false,
        });
      } else {
        // 如果都没有命中，就走下载逻辑
        this.downloadFile();
      }
    },

    /**
     * 下载文件
     */
    downloadFile() {
      const url = this.downloadUrl;

      if (isWXWork || isWeixin || isQQ) {
        this.wxappDownloadFile();
      } else {
        Utils.downloadFile(url, this.data.fileName);
      }
    },

    /**
     * 腾讯三件套内部下载逻辑
     */
    wxappDownloadFile() {
      const url = this.downloadUrl;
      const text = `
      <div
      style="user-select: all;-webkit-box-orient: vertical;display: -webkit-box;-webkit-line-clamp:2;overflow:hidden;"
      >${url}</div>
      `;
      this.$Confirm({
        title: '复制链接到浏览器即可下载文件',
        text,
        confirmText: '一键复制',
        cancelText: '关闭',
      }).then(() => {
        this.$copyText(url)
          .then(() => {
            this.$Toast(this.$t('chat_add_friend_copy_success_msg'));
          }, () => {
            this.$Toast({ text: '复制失败，请重试', type: 'error' });;
          });
      })
        .catch(() => {});
    },

    /**
     * 停止上传
     */
    stopUpload() {
      this.data?.progressController?.abort();
    },

    /**
     * 为老webim漫游消息获取下载地址
     */
    async getDownloadUrlForOldMsg() {
      const extData = this.data?.crm?.msg_webim_extra_data;
      const extuin = extData?.uint64_extuin || extData?.uint64_robotid || 0; // 人工客服或者机器人的id

      if (!extuin) {
        return '';
      }

      const postData = {
        uint64_kfuin: Number(this.wpaDetail.kfuin),
        uint32_type: 2, // 2代表文件
        uint32_channel: 3, // webim固定3
        uint64_kfext: Number(extuin), // 工号
        str_id: base64.encode(this.data.uuid), // 需要base64编码
        uint64_msg_time: this.data.time * 1000,   // 消息时间戳即可(单位us)
      };

      try {
        const res = await this.$ajax.post(GET_FILE_DOWNLOAD_URL_FOR_OLD_MSG, postData);
        const rs = res.data;
        if (rs?.code === 0) {
        // 返回数据需要base64解码
          let downloadUrl = base64.decode(rs.data.str_url);
          // 替换http协议为https。
          downloadUrl = downloadUrl.replace(/^http:\/\//, 'https://');
          return downloadUrl;
        }
        return '';
      } catch (ex) {
        return '';
      }
    },
  },
};
</script>

<style lang="less">
.msg-file {
  position: relative;
  display: inline-block;
  vertical-align: middle;
  .info {
    position: relative;
    width: 240px;
    height: 56px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    &.file-pointer{
      cursor: pointer;
    }
    .icon {
      padding: 8px;
      img {
        width: 40px;
        height: 40px;
        display: block;
      }
    }
    .detail {
      flex: 1;
      text-align: left;
      min-width: 0px;
      padding: 8px 8px 8px 0px;
      .name {
        font-size: 13px;
        line-height: 18px;
        color: #262d3d;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin-bottom: 5px;
      }
      .size, .percent {
        font-size: 12px;
        line-height: 17px;
        color: #bac1cf;
      }
      .percent{
        margin-left: 8px;
      }
    }
    .progress{
      position: absolute;
      z-index: 1;
      height: 100%;
      top: 0;
      left: 0;
      width: 0;
      opacity: .1;
      background: #0067ed;
      border-radius: inherit;
    }
  }
  // 操作按钮
  .operator{
    cursor: pointer;
    position: relative;
    z-index: 10;
    display: flex;
    width: 32px;
    height: 32px;
    justify-content: center;
    align-items: center;
    border-radius: 3px;
    margin-right: 8px;
    transition: all .3s;
    &:hover{
      background: rgba(36, 56, 97, 0.05);
    }
    &:active{
      background: #F1F6FF;
    }
  }
}

.msg-b2c .msg-file .file-download-icon{
  left: auto;
  right: -36px;
}

// // PC页面不显示下载按钮
// .html-is-pc{
//   .msg-file .file-download-icon{
//     display: none;
//   }
// }
</style>
